/** Design tokens — single source of truth (Fortune-500 blueprint Part 3.1).
 *
 *  Used by every primitive in /design/components and by any bespoke surface.
 *  When something looks "off" — pick a token, not a hex.
 */

export const color = {
  // Dark (broadcast / professional surfaces)
  navy900: "#0A1628",
  navy800: "#0F1E33",
  navy700: "#13233B",
  line:    "#1E3050",
  // Brand accents
  cyan400: "#22D3EE",
  cyan600: "#0891B2",
  teal400: "#14B8A6",
  teal600: "#0D9488",
  green500: "#10B981",
  amber500: "#F59E0B",
  red500:   "#EF4444",
  ice:      "#CADCFC",
  slate400: "#94A3B8",
  slate500: "#64748B",
  // Light (consumer surfaces)
  white:    "#FFFFFF",
  paper:    "#F8FAFC",
  border:   "#E2E8F0",
  ink:      "#0A1628",
  // Audience-aware accents
  audience: {
    station: "#0891B2",   // cyan-600
    agency:  "#0D9488",   // teal-600
    brand:   "#B45309",   // amber-700
  },
  // Confidence label colors — used by ConfidenceBadge
  confidence: {
    VERIFIED: "#10B981",   // green
    DIRECT:   "#22D3EE",   // cyan
    SOCIAL:   "#A78BFA",   // violet
    PANEL:    "#F59E0B",   // amber
    HYBRID:   "#0EA5E9",   // sky
    MODEL:    "#94A3B8",   // slate
  },
};

export const font = {
  // Stacks — set the actual <link> on App boot. Fraunces serif for display
  // + headings, Inter for body, IBM Plex Mono for numbers (so figures align).
  serif: `"Fraunces", "Source Serif Pro", Georgia, serif`,
  sans:  `"Inter", system-ui, -apple-system, "Segoe UI", sans-serif`,
  mono:  `"IBM Plex Mono", "JetBrains Mono", ui-monospace, monospace`,
};

// size / line-height pairs
export const type = {
  display: { fontSize: 40, lineHeight: "48px", fontWeight: 700, fontFamily: font.serif, letterSpacing: "-0.02em" },
  h1:      { fontSize: 30, lineHeight: "38px", fontWeight: 700, fontFamily: font.serif, letterSpacing: "-0.015em" },
  h2:      { fontSize: 24, lineHeight: "32px", fontWeight: 700, fontFamily: font.serif },
  h3:      { fontSize: 18, lineHeight: "26px", fontWeight: 600, fontFamily: font.sans },
  body:    { fontSize: 15, lineHeight: "24px", fontWeight: 400, fontFamily: font.sans },
  small:   { fontSize: 13, lineHeight: "20px", fontWeight: 400, fontFamily: font.sans },
  micro:   { fontSize: 11, lineHeight: "16px", fontWeight: 600, fontFamily: font.sans, letterSpacing: "0.06em", textTransform: "uppercase" },
  number:  { fontFamily: font.mono, fontWeight: 600 },
};

export const space = [0, 4, 8, 12, 16, 20, 24, 32, 40, 56, 72];

export const radius = {
  sm: 6,
  md: 10,
  lg: 14,
  xl: 20,
  pill: 999,
};

export const elevation = {
  e1: "0 1px 3px rgba(10,22,40,0.06)",
  e2: "0 4px 20px rgba(10,22,40,0.10)",
  e3: "0 12px 32px rgba(0,0,0,0.28)",
};

export const motion = {
  fast: "120ms",
  base: "200ms",
  slow: "320ms",
  ease: "cubic-bezier(0.2, 0.7, 0.2, 1)",
};

// Convenience — common transition string
export const transition = (props = ["all"]) =>
  props.map((p) => `${p} ${motion.base} ${motion.ease}`).join(", ");

// CSS variable injection helper — call once in App boot if you want
// `var(--navy-900)` etc. available. For now everything reads from JS.
export function cssVars(theme = "dark") {
  const map = theme === "dark"
    ? { bg: color.navy900, surface: color.navy800, line: color.line, text: color.white, muted: color.slate400 }
    : { bg: color.paper,   surface: color.white,   line: color.border, text: color.ink,   muted: color.slate500 };
  return Object.fromEntries(Object.entries(map).map(([k, v]) => [`--ws-${k}`, v]));
}

const tokens = { color, font, type, space, radius, elevation, motion, transition, cssVars };
export default tokens;
